import { useCallback, useEffect, useRef, useState } from 'react';  // Added useState
import type { Placement } from '@react-types/overlays';
import { ChevronSelectorVertical } from '@untitledui/icons';
import { FolderClosed, Plus, FolderPlus } from '@untitledui/icons';
import { useFocusManager } from "react-aria";
import type { DialogProps as AriaDialogProps } from 'react-aria-components';
import {
    Button as AriaButton,
    Dialog as AriaDialog,
    DialogTrigger as AriaDialogTrigger,
    Popover as AriaPopover,
} from 'react-aria-components';
import { Button } from '@/components/base/buttons/button';
import { RadioButtonBase } from '@/components/base/radio-buttons/radio-buttons';
import { useBreakpoint } from '@/hooks/use-breakpoint';
import { cx } from '@/utils/cx';
import { useProject, type Project } from '@/providers/project-provider';
import { useAuth } from '@/providers/auth-provider';  // Added import
import { HmrcConnectDialog, HmrcUserType } from '@/components/application/hmrc/hmrc-connect-dialog';  // Added import

export const NavAccountMenu = ({
    className,
    selectedProjectId,
    projects = [],
    onProjectChange,
    ...dialogProps
}: AriaDialogProps & {
    className?: string;
    projects?: Project[];
    selectedProjectId?: string;
    onProjectChange?: (projectId: string) => void;
}) => {
    const focusManager = useFocusManager();
    const dialogRef = useRef<HTMLDivElement>(null);
    const { user } = useAuth();  // Added for handleUserTypeConnect
    const [isConnecting, setIsConnecting] = useState(false);  // Added for connection state

    // Copied and adapted from obligations.tsx
    const handleUserTypeConnect = async (userType: HmrcUserType, projectId: string) => {
        if (!user?.id) return;

        try {
            setIsConnecting(true);

            const apiUrl = import.meta.env.VITE_API_URL;

            const response = await fetch(
                `${apiUrl}/api/hmrc/oauth/connect?user_uuid=${encodeURIComponent(
                    user.id,
                )}&usertype=${encodeURIComponent(userType)}&project_id=${encodeURIComponent(projectId)}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            );

            if (!response.ok) {
                throw new Error('Failed to initiate HMRC connection');
            }

            const data = await response.json();

            if (data.authUrl) {
                window.location.href = data.authUrl;
            }
        } catch (error) {
            console.error('Error connecting to HMRC:', error);
        } finally {
            setIsConnecting(false);
        }
    };

    const onKeyDown = useCallback(
        (e: KeyboardEvent) => {
            switch (e.key) {
                case 'ArrowDown':
                    focusManager?.focusNext({ tabbable: true, wrap: true });
                    break;
                case 'ArrowUp':
                    focusManager?.focusPrevious({ tabbable: true, wrap: true });
                    break;
            }
        },
        [focusManager],
    );

    useEffect(() => {
        const element = dialogRef.current;
        if (element) {
            element.addEventListener('keydown', onKeyDown);
            return () => {
                if (element) {
                    element.removeEventListener('keydown', onKeyDown);
                }
            };
        }
    }, [onKeyDown]);

    return (
        <AriaDialog
            {...dialogProps}
            ref={dialogRef}
            className={cx(
                'w-66 rounded-xl bg-secondary-alt shadow-lg ring ring-secondary-alt outline-hidden',
                className,
            )}
        >
            <div className="rounded-xl bg-primary ring-1 ring-secondary">
                <div className="flex flex-col gap-0.5 border-b border-secondary py-1.5">
                    <div className="px-3 pt-1.5 pb-1 text-xs font-semibold text-tertiary">
                        Switch Project
                    </div>

                    <div className="flex flex-col gap-0.5 px-1.5">
                        {projects.map((project) => (
                            <button
                                key={project.id}
                                onClick={() => onProjectChange?.(project.id)}
                                className={cx(
                                    'relative w-full cursor-pointer rounded-md px-4 py-2.5 text-left outline-focus-ring hover:bg-primary-hover focus:z-10 focus-visible:outline-2 focus-visible:outline-offset-2',
                                    project.id === selectedProjectId && 'bg-primary-hover',
                                )}
                            >
                                <div className="flex items-center gap-3">
                                    <FolderClosed className="size-5 shrink-0 text-fg-quaternary" />
                                    <div className="flex-1">
                                        <p className="text-sm font-semibold text-secondary">{project.name}</p>
                                        {project.type && (
                                            <p className="text-xs text-tertiary capitalize">{project.type}</p>
                                        )}
                                    </div>
                                    <RadioButtonBase
                                        isSelected={project.id === selectedProjectId}
                                        className="shrink-0"
                                    />
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Updated: Wrap the "Add Project" button with HmrcConnectDialog */}
                <div className="flex flex-row gap-2 px-2 pt-0.5 pb-2">
                    <HmrcConnectDialog
                        trigger={
                            <Button iconLeading={<Plus />} color="secondary" size="sm" className="flex-1">
                                Add
                            </Button>
                        }
                        onConnect={handleUserTypeConnect}
                        isConnecting={isConnecting}
                    />
                    <Button iconLeading={<FolderPlus />} color="secondary" size="sm" className="flex-1">
                        View All
                    </Button>
                </div>
            </div>
        </AriaDialog>
    );
};

export const NavAccountCard = ({
    popoverPlacement,
}: {
    popoverPlacement?: Placement;
}) => {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const isDesktop = useBreakpoint('lg');
    const { currentProjectId, projects, setCurrentProjectId, loading } = useProject();

    const selectedProject = projects.find((project) => project.id === currentProjectId);

    const handleProjectChange = (projectId: string) => {
        setCurrentProjectId(projectId);
    };

    if (loading) {
        return (
            <div className="flex w-full items-center gap-3 rounded-xl p-3 ring-1 ring-secondary ring-inset">
                <div className="h-10 w-10 animate-pulse rounded-full bg-secondary" />
                <div className="flex-1 space-y-2">
                    <div className="h-4 animate-pulse rounded bg-secondary" />
                    <div className="h-3 w-2/3 animate-pulse rounded bg-secondary" />
                </div>
            </div>
        );
    }

    if (!selectedProject || projects.length === 0) {
        return (
            <div className="rounded-xl bg-primary p-3 ring-1 ring-secondary ring-inset">
                <p className="text-sm text-tertiary">No projects available</p>
            </div>
        );
    }

    return (
        <AriaDialogTrigger>
            <AriaButton
                ref={triggerRef}
                className={cx(
                    'relative flex w-full items-center gap-3 rounded-xl p-3 ring-1 ring-secondary ring-inset',
                    'cursor-pointer transition duration-100 ease-linear hover:bg-primary-hover',
                    'focus-visible:outline-2 focus-visible:outline-offset-2',
                )}
            >
                <FolderClosed className="size-10 shrink-0 text-fg-quaternary" />
                <div className="flex-1 text-left">
                    <p className="text-sm font-semibold text-secondary">{selectedProject.name}</p>
                    {selectedProject.type && (
                        <p className="text-xs text-tertiary capitalize">{selectedProject.type}</p>
                    )}
                </div>
                <ChevronSelectorVertical className="size-4 shrink-0 ml-auto text-fg-quaternary" />
            </AriaButton>

            <AriaPopover
                placement={popoverPlacement ?? (isDesktop ? 'right bottom' : 'top right')}
                triggerRef={triggerRef}
                offset={8}
                className={({ isEntering, isExiting }) =>
                    cx(
                        'origin-[--trigger-anchor-point] will-change-transform',
                        isEntering &&
                        'duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5',
                        isExiting &&
                        'duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5',
                    )
                }
            >
                <NavAccountMenu
                    selectedProjectId={currentProjectId || undefined}
                    projects={projects}
                    onProjectChange={handleProjectChange}
                />
            </AriaPopover>
        </AriaDialogTrigger>
    );
};